import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const RegisterForm = ({ onSuccess }) => {
  const { signUp } = useAuth();
  const [formData, setFormData] = useState({ firstName: '', lastName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, feedback: [] });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (name === 'password') checkPasswordStrength(value);
  };

  const checkPasswordStrength = (password) => {
    const feedback = [];
    let score = 0;
    if (password?.length >= 8) score += 1; else feedback?.push('At least 8 characters');
    if (/[A-Z]/?.test(password)) score += 1; else feedback?.push('One uppercase letter');
    if (/[a-z]/?.test(password)) score += 1; else feedback?.push('One lowercase letter');
    if (/\d/?.test(password)) score += 1; else feedback?.push('One number');
    if (/[!@#$%^&*(),.?":{}|<>]/?.test(password)) score += 1; else feedback?.push('One special character');
    setPasswordStrength({ score, feedback });
  };

  const getPasswordStrengthColor = () => {
    if (passwordStrength?.score <= 1) return 'bg-error';
    if (passwordStrength?.score <= 3) return 'bg-warning';
    return 'bg-success';
  };

  const getPasswordStrengthText = () => {
    if (passwordStrength?.score <= 1) return 'Weak';
    if (passwordStrength?.score <= 3) return 'Medium';
    return 'Strong';
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData?.firstName?.trim()) newErrors.firstName = 'First name is required';
    if (!formData?.lastName?.trim()) newErrors.lastName = 'Last name is required';
    if (!formData?.email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/?.test(formData?.email)) newErrors.email = 'Please enter a valid email address';
    if (!formData?.password) newErrors.password = 'Password is required';
    else if (passwordStrength?.score < 3) newErrors.password = 'Password is too weak';
    if (!formData?.confirmPassword) newErrors.confirmPassword = 'Please confirm your password';
    else if (formData?.password !== formData?.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    if (!formData?.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors)?.length > 0) { setErrors(newErrors); return; }
    setIsLoading(true);
    try {
      const { data, error } = await signUp(formData?.email, formData?.password, {
        full_name: `${formData?.firstName} ${formData?.lastName}`,
        role: 'member'
      });
      if (error) {
        setErrors({ general: error?.message || 'Registration failed. Please try again.' });
      } else if (data?.user) {
        onSuccess();
      }
    } catch (err) {
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors?.general && (
        <div className="p-4 bg-error-50 border border-error-100 rounded-lg">
          <div className="flex items-center space-x-2">
            <Icon name="AlertCircle" size={16} color="var(--color-error)" />
            <p className="text-sm text-error">{errors?.general}</p>
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label htmlFor="firstName" className="block text-sm font-medium text-text-primary mb-2">First name</label>
          <input type="text" id="firstName" name="firstName" value={formData?.firstName} onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.firstName ? 'border-error' : 'border-border'}`}
            placeholder="Alex" />
          {errors?.firstName && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.firstName}</span></p>}
        </div>
        <div>
          <label htmlFor="lastName" className="block text-sm font-medium text-text-primary mb-2">Last name</label>
          <input type="text" id="lastName" name="lastName" value={formData?.lastName} onChange={handleInputChange}
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.lastName ? 'border-error' : 'border-border'}`}
            placeholder="Morgan" />
          {errors?.lastName && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.lastName}</span></p>}
        </div>
      </div>
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-text-primary mb-2">Email address</label>
        <div className="relative">
          <input type="email" id="email" name="email" value={formData?.email} onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.email ? 'border-error' : 'border-border'}`}
            placeholder="alex@company.com" />
          <Icon name="Mail" size={20} color={errors?.email ? 'var(--color-error)' : 'var(--color-secondary-400)'} className="absolute left-4 top-1/2 transform -translate-y-1/2" />
        </div>
        {errors?.email && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.email}</span></p>}
      </div>
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-text-primary mb-2">Password</label>
        <div className="relative">
          <input type={showPassword ? 'text' : 'password'} id="password" name="password" value={formData?.password} onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.password ? 'border-error' : 'border-border'}`}
            placeholder="Create a strong password" />
          <Icon name="Lock" size={20} color={errors?.password ? 'var(--color-error)' : 'var(--color-secondary-400)'} className="absolute left-4 top-1/2 transform -translate-y-1/2" />
          <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-text-primary">
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {formData?.password && (
          <div className="mt-2">
            <div className="flex space-x-1 mb-1">
              {[1,2,3,4,5]?.map(i => (
                <div key={i} className={`h-1 flex-1 rounded-full ${i <= passwordStrength?.score ? getPasswordStrengthColor() : 'bg-secondary-200'}`} />
              ))}
            </div>
            <p className="text-xs text-text-secondary">{getPasswordStrengthText()}</p>
          </div>
        )}
        {errors?.password && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.password}</span></p>}
      </div>
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-text-primary mb-2">Confirm password</label>
        <div className="relative">
          <input type={showConfirmPassword ? 'text' : 'password'} id="confirmPassword" name="confirmPassword" value={formData?.confirmPassword} onChange={handleInputChange}
            className={`w-full px-4 py-3 pl-12 pr-12 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200 ${errors?.confirmPassword ? 'border-error' : 'border-border'}`}
            placeholder="Confirm your password" />
          <Icon name="Lock" size={20} color={errors?.confirmPassword ? 'var(--color-error)' : 'var(--color-secondary-400)'} className="absolute left-4 top-1/2 transform -translate-y-1/2" />
          <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-text-primary">
            <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>
        {errors?.confirmPassword && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.confirmPassword}</span></p>}
      </div>
      <div>
        <label className="flex items-start space-x-3 cursor-pointer">
          <input type="checkbox" name="agreeToTerms" checked={formData?.agreeToTerms} onChange={handleInputChange} className="w-4 h-4 mt-0.5 text-primary border-border rounded focus:ring-primary-500" />
          <span className="text-sm text-text-secondary">
            I agree to the <button type="button" className="text-primary hover:text-primary-700">Terms of Service</button> and <button type="button" className="text-primary hover:text-primary-700">Privacy Policy</button>
          </span>
        </label>
        {errors?.agreeToTerms && <p className="mt-1 text-sm text-error flex items-center space-x-1"><Icon name="AlertCircle" size={14} /><span>{errors?.agreeToTerms}</span></p>}
      </div>
      <button type="submit" disabled={isLoading}
        className="w-full bg-primary text-white py-3 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200 font-medium flex items-center justify-center space-x-2 disabled:opacity-50">
        {isLoading ? (
          <><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /><span>Creating account...</span></>
        ) : (
          <><Icon name="UserPlus" size={20} /><span>Create Account</span></>
        )}
      </button>
    </form>
  );
};

export default RegisterForm;